/**
 * SequenceEditFormBody — shared form body for CREATE / ALTER SEQUENCE.
 *
 * Engines:
 *   - mssql / postgres / oracle: full editor (data-type field gated to
 *     mssql + postgres — Oracle does not accept AS <type> on CREATE
 *     SEQUENCE).
 *   - mysql / sqlite: unsupported affordance with documented redirect
 *     tooltip (AUTO_INCREMENT / AUTOINCREMENT respectively).
 *
 * Modes:
 *   - `create`: builds a `SequenceDef` and dispatches `commitCreateSequence`.
 *   - `alter`: builds a `SequenceChange` and dispatches `commitAlterSequence`.
 *
 * The owning popover / Designer pane seeds `initialValues` from the parked
 * statement; the form is the same body whether mounted inline (Variant 1)
 * or in the Designer pane (Variant 2).
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D3 — Sequence UI.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import type { QualifiedName } from '../../../../sql-grammar/model';
import {
  commitAlterSequence,
  getAlterSequenceUnsupportedTooltip,
  isAlterSequenceSupported,
  validateAlterSequenceForEngine,
} from '../../edit/edits/alterSequence';
import {
  commitCreateSequence,
  getCreateSequenceUnsupportedTooltip,
  isCreateSequenceSupported,
  validateCreateSequenceForEngine,
} from '../../edit/edits/createSequence';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { SequenceFormFields } from './SequenceFormFields';
import {
  buildSequenceChange,
  buildSequenceDef,
  type SequenceFormValues,
} from './sequenceFormHelpers';
import { FormActionsSlot } from './FormActionsSlot';
import { FormEngineChip } from './FormEngineChip';

export type SequenceEditMode = 'create' | 'alter';

export interface SequenceEditFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly mode: SequenceEditMode;
  readonly sequenceName: QualifiedName;
  readonly initialValues?: SequenceFormValues;
  readonly testId: string;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

export function SequenceEditFormBody(props: SequenceEditFormBodyProps) {
  const {
    editContext, mode, sequenceName, initialValues, testId, onCommitted, onCancel,
  } = props;
  const engine = editContext.service.engineId;
  const supported =
    mode === 'create' ? isCreateSequenceSupported(engine) : isAlterSequenceSupported(engine);
  const unsupportedTooltip =
    mode === 'create'
      ? getCreateSequenceUnsupportedTooltip(engine)
      : getAlterSequenceUnsupportedTooltip(engine);

  const [values, setValues] = useState<SequenceFormValues>({
    name: initialValues?.name ?? sequenceName.name,
    dataType: initialValues?.dataType ?? '',
    startWith: initialValues?.startWith ?? '',
    incrementBy: initialValues?.incrementBy ?? '',
    minValue: initialValues?.minValue ?? '',
    maxValue: initialValues?.maxValue ?? '',
    cycle: initialValues?.cycle ?? '',
    cache: initialValues?.cache ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (!supported) {
    return (
      <div data-testid={testId} className="flex flex-col gap-3">
        <div className="text-xs font-semibold">
          {mode === 'create' ? 'Create sequence' : 'Edit sequence'}
        </div>
        <div
          className="rounded-md border border-border bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground"
          data-testid={`${testId}-unsupported`}
        >
          {unsupportedTooltip}
        </div>
        <div className="flex items-center justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onCancel?.()}
            data-testid={`${testId}-close`}
          >
            Close
          </Button>
        </div>
      </div>
    );
  }

  const update = <K extends keyof SequenceFormValues>(
    key: K,
    value: SequenceFormValues[K],
  ) => setValues((prev) => ({ ...prev, [key]: value }));

  async function handleSubmit() {
    setError(null);
    if (mode === 'create') {
      const sequence = buildSequenceDef(values, engine, sequenceName);
      const validationError = validateCreateSequenceForEngine(engine, { sequence });
      if (validationError !== null) {
        setError(validationError);
        return;
      }
      setPending(true);
      try {
        await commitCreateSequence(editContext, { sequence });
        onCommitted?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setPending(false);
      }
      return;
    }
    const change = buildSequenceChange(values);
    const args = { sequenceName, change };
    const validationError = validateAlterSequenceForEngine(engine, args);
    if (validationError !== null) {
      setError(validationError);
      return;
    }
    setPending(true);
    try {
      await commitAlterSequence(editContext, args);
      onCommitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPending(false);
    }
  }

  const submitLabel = pending
    ? 'Saving…'
    // Title already says "Create sequence" — the CTA just names the verb.
    : mode === 'create' ? 'Create' : 'Save changes';

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">
          {mode === 'create' ? 'Create sequence' : 'Edit sequence'}
        </div>
        <FormEngineChip engine={engine} />
      </div>

      <SequenceFormFields
        engine={engine}
        mode={mode}
        values={values}
        onChange={update}
        testId={testId}
      />

      <span className="text-[10px] text-muted-foreground">
        Type <code>NONE</code> in Min value, Max value or Cache to remove that
        limit — the statement gets <code>NO MINVALUE</code> /{' '}
        <code>NO MAXVALUE</code> / <code>NO CACHE</code>{' '}
        (<code>NOMINVALUE</code> / <code>NOMAXVALUE</code> / <code>NOCACHE</code>{' '}
        on Oracle).
      </span>

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
        >
          {error}
        </div>
      ) : null}

      <FormActionsSlot className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onCancel?.()}
          data-testid={`${testId}-cancel`}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={pending}
          onClick={() => void handleSubmit()}
          data-testid={`${testId}-submit`}
        >
          {submitLabel}
        </Button>
      </FormActionsSlot>
    </div>
  );
}
